import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  CoverImageBlock,
  CoverImageBlockV2,
  PdfCoverImageBlock,
  QuoteBlock
} from '@sequencehq/quote-content'
import { useGetSignedAssetUrl } from '@sequencehq/utils'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'

export const COVER_IMAGE_ACCEPTED_TYPES = 'image/jpeg,image/png'

const useSelectPresentation = () => {
  const cubeContext = useCubeContext()
  return cubeContext.queries.rawData.data.presentation
}

const useSelectCoverImageBlock = () => {
  const coverImageBlock = useSelectPresentation().blocks.find(
    block => block.blockType === 'coverImage'
  )

  if (!coverImageBlock) {
    return undefined
  }

  if (
    !isCoverImageBlockV2(coverImageBlock) &&
    !isCoverImageBlock(coverImageBlock)
  ) {
    return undefined
  }

  return coverImageBlock
}

export const useSelectSignedCoverImageSrc = () => {
  const coverImageBlockSrc = useSelectCoverImageSrc()

  const signedUrl = useGetSignedAssetUrl({
    url: coverImageBlockSrc ?? '',
    assetLoader: async ({ assetId }) => {
      const res = await dashboard20240730Client.getAsset({ id: assetId })

      if (!res.data || res.error) {
        return undefined
      }

      return { urlSigned: res.data.url }
    }
  })

  return signedUrl
}

export const useSelectCoverImageSrc = () => {
  const coverImageBlock = useSelectCoverImageBlock()

  if (!coverImageBlock) {
    return undefined
  }

  if (isCoverImageBlockV2(coverImageBlock)) {
    return coverImageBlock.config.assetPath
  }

  if (isCoverImageBlock(coverImageBlock)) {
    return coverImageBlock.config.asset.url
  }

  return undefined
}

export const useStoreCoverImageAsset = () => {
  const cubeContext = useCubeContext()

  const presentation = useSelectPresentation()
  const existingBlock = useSelectCoverImageBlock()

  return (assetPath: string | null) => {
    let newPresentation = { ...presentation }

    if (assetPath === null) {
      if (existingBlock) {
        cubeContext.mutators.updateData({
          presentation: {
            ...presentation,
            blocks: presentation.blocks.filter(
              block => block.blockType !== 'coverImage'
            )
          }
        })
      }
      return
    }

    const newCoverImageBlock: QuoteBlock = {
      blockType: 'coverImage',
      version: 2,
      config: {
        assetPath
      }
    }

    if (existingBlock) {
      newPresentation = {
        ...presentation,
        blocks: presentation.blocks.map(block =>
          block.blockType === 'coverImage' ? newCoverImageBlock : block
        )
      }
    } else {
      newPresentation = {
        ...presentation,
        blocks: [newCoverImageBlock, ...presentation.blocks]
      }
    }

    /**
     * Enable the `showPdfCoverPage` setting when updating the cover image
     */
    newPresentation = {
      ...newPresentation,
      version: 3,
      settings: {
        ...newPresentation.settings,
        showPdfCoverPage: true
      }
    }

    cubeContext.mutators.updateData({
      presentation: newPresentation
    })
  }
}

const useSelectPdfCoverImageBlock = () => {
  const pdfCoverImageBlock = useSelectPresentation().blocks.find(
    block => block.blockType === 'pdfCoverImage'
  )

  if (!pdfCoverImageBlock) {
    return undefined
  }

  if (!isPdfCoverImageBlock(pdfCoverImageBlock)) {
    return undefined
  }

  return pdfCoverImageBlock
}

export const useSelectPdfCoverImageSrc = () => {
  const pdfCoverImageBlock = useSelectPdfCoverImageBlock()

  if (!pdfCoverImageBlock) {
    return undefined
  }

  return pdfCoverImageBlock.config.assetPath
}

export const useSelectSignedPdfCoverImageSrc = () => {
  const pdfCoverImageSrc = useSelectPdfCoverImageSrc()

  const signedUrl = useGetSignedAssetUrl({
    url: pdfCoverImageSrc ?? '',
    assetLoader: async ({ assetId }) => {
      const res = await dashboard20240730Client.getAsset({ id: assetId })

      if (!res.data || res.error) {
        return undefined
      }

      return { urlSigned: res.data.url }
    }
  })

  return signedUrl
}

export const useStorePdfCoverImageAsset = () => {
  const cubeContext = useCubeContext()

  const presentation = useSelectPresentation()
  const existingBlock = useSelectPdfCoverImageBlock()

  return (assetPath: string | null) => {
    if (assetPath === null) {
      if (!existingBlock) return
      cubeContext.mutators.updateData({
        presentation: {
          ...presentation,
          blocks: presentation.blocks.filter(
            block => block.blockType !== 'pdfCoverImage'
          )
        }
      })
      return
    }

    const newPdfCoverImageBlock: QuoteBlock = {
      blockType: 'pdfCoverImage',
      version: 1,
      config: { assetPath }
    }

    const newBlocks = existingBlock
      ? presentation.blocks.map(block =>
          block.blockType === 'pdfCoverImage' ? newPdfCoverImageBlock : block
        )
      : insertAfterCoverImage(presentation.blocks, newPdfCoverImageBlock)

    cubeContext.mutators.updateData({
      presentation: { ...presentation, blocks: newBlocks }
    })
  }
}

const isCoverImageBlock = (block: QuoteBlock): block is CoverImageBlock =>
  block.blockType === 'coverImage' && block.version === 1

const isCoverImageBlockV2 = (block: QuoteBlock): block is CoverImageBlockV2 =>
  block.blockType === 'coverImage' && block.version === 2

const isPdfCoverImageBlock = (block: QuoteBlock): block is PdfCoverImageBlock =>
  block.blockType === 'pdfCoverImage' && block.version === 1

const insertAfterCoverImage = (
  blocks: QuoteBlock[],
  newBlock: QuoteBlock
): QuoteBlock[] => {
  const coverImageIndex = blocks.findIndex(
    block => block.blockType === 'coverImage'
  )
  const insertIndex = coverImageIndex >= 0 ? coverImageIndex + 1 : 0
  return [
    ...blocks.slice(0, insertIndex),
    newBlock,
    ...blocks.slice(insertIndex)
  ]
}
